import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { OfferProperties } from '@features/offers/interfaces/offersInterface';
import {
  buildTasOptionsByType,
  getTasDependentFieldUpdates,
  getTasFieldName,
  getTasRuleState,
  RulesTASRelational,
  TasRuleState,
  TasRuleType,
} from '@features/offers/utils/tasRelational';
import {
  OfferBaseFormData,
  ProductFormData,
} from '@features/products/interfaces/productInterface';
import {
  getMonthlyOptions,
  MONTHLY_ACQUISITION_ID,
  MONTHLY_SEASONALITY_ID,
} from '@features/products/utils/offerBase';
import { SelectController } from '@organisms';
import { Button, Col, Divider, Modal, Row, Tag, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import {
  Control,
  FieldArrayWithId,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { CurrencyController } from 'src/app/framework/organisms/Controllers/CurrencyController.organism';

type ListOfferBaseProps = {
  control: Control<ProductFormData, any, ProductFormData>;
  setValue: UseFormSetValue<ProductFormData>;
  fields: FieldArrayWithId<ProductFormData, 'offers', 'id'>[];
  offersValues: OfferBaseFormData[];
  offerProperties: OfferProperties;
  rulesTASRelational: RulesTASRelational[];
  handleAddSectionOffer: (offer: OfferBaseFormData) => void;
  handleRemoveSectionOffer: (index: number) => void;
  isReadOnly?: boolean;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  isMonthly?: boolean;
};

export function ListOfferBase({
  control,
  setValue,
  fields,
  offersValues,
  offerProperties,
  rulesTASRelational,
  handleAddSectionOffer,
  handleRemoveSectionOffer,
  isReadOnly = false,
  showAddButton = true,
  showRemoveButton = true,
  isMonthly = false,
}: ListOfferBaseProps) {
  const watchedOffers = useWatch({
    control,
    name: 'offers',
  });
  const resolvedOffersValues =
    watchedOffers && watchedOffers.length > 0 ? watchedOffers : offersValues;

  const baseOptionsByType = useMemo(
    () => buildTasOptionsByType(offerProperties),
    [offerProperties]
  );

  const rulesStateByOffer = useMemo<TasRuleState[]>(() => {
    const monthlyOptions = getMonthlyOptions(baseOptionsByType);

    return resolvedOffersValues.map(offer => {
      const baseRuleState = getTasRuleState(
        offer,
        baseOptionsByType,
        isReadOnly,
        rulesTASRelational
      );

      if (!isMonthly || isReadOnly) return baseRuleState;

      return {
        ...baseRuleState,
        optionsByType: {
          ...baseRuleState.optionsByType,
          acquisition: monthlyOptions.acquisition,
          seasonality: monthlyOptions.seasonality,
        },
        disabledByType: {
          ...baseRuleState.disabledByType,
          acquisition: true,
          seasonality: true,
        },
        fixedValueByType: {
          ...baseRuleState.fixedValueByType,
          acquisition: MONTHLY_ACQUISITION_ID,
          seasonality: MONTHLY_SEASONALITY_ID,
        },
      };
    });
  }, [baseOptionsByType, isMonthly, isReadOnly, resolvedOffersValues]);

  const syncTasDependentFields = (
    index: number,
    offer: OfferBaseFormData,
    ruleState: TasRuleState
  ) => {
    getTasDependentFieldUpdates(offer, ruleState).forEach(
      ({ fieldName, value }) => {
        setValue(`offers.${index}.${fieldName}`, value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    );
  };

  const handleTasChange = (
    index: number,
    type: TasRuleType,
    value?: string | number
  ) => {
    const fieldName = getTasFieldName(type);
    const normalizedValue = value ? String(value) : '';
    const nextOffer = {
      ...resolvedOffersValues[index],
      [fieldName]: normalizedValue,
    };

    setValue(`offers.${index}.${fieldName}`, normalizedValue, {
      shouldDirty: true,
      shouldValidate: true,
    });

    syncTasDependentFields(
      index,
      nextOffer,
      getTasRuleState(
        nextOffer,
        baseOptionsByType,
        isReadOnly,
        rulesTASRelational
      )
    );
  };

  useEffect(() => {
    if (isReadOnly) return;

    rulesStateByOffer.forEach((ruleState, index) => {
      syncTasDependentFields(index, resolvedOffersValues[index], ruleState);
    });
  }, [isReadOnly, resolvedOffersValues, rulesStateByOffer, setValue]);

  return (
    <>
      <Divider />
      <Typography.Title level={5}>Ofertas Base</Typography.Title>
      {fields.map((item, index) => {
        const currentOffer = resolvedOffersValues[index];
        const ruleState = rulesStateByOffer[index];
        const isDuplicate =
          currentOffer &&
          currentOffer.acquisitionId &&
          currentOffer.seasonalityId &&
          currentOffer.typeId &&
          resolvedOffersValues.some(
            (offer, i) =>
              i !== index &&
              offer.acquisitionId === currentOffer.acquisitionId &&
              offer.seasonalityId === currentOffer.seasonalityId &&
              offer.typeId === currentOffer.typeId
          );

        return (
          <div
            key={item.id}
            style={{
              marginBottom: '16px',
              border: isDuplicate ? '2px solid #ff4d4f' : '1px solid #434343',
              borderRadius: '8px',
              position: 'relative',
            }}
          >
            <Row
              justify='space-between'
              align='middle'
              style={{ padding: '12px 16px' }}
            >
              <Col>
                <Typography.Text
                  strong
                >{`Oferta ${index + 1}`}</Typography.Text>
              </Col>
              <Col>
                {currentOffer?.isFallbackOffer && (
                  <Tag color='blue'>Oferta Fallback</Tag>
                )}
                {showRemoveButton && index > 0 && (
                  <Button
                    type='text'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const offer = resolvedOffersValues[index];
                      const hasAnyValue =
                        (!!offer?.fullPrice &&
                          !['0.00', '0,00'].includes(offer.fullPrice)) ||
                        !!offer?.acquisitionId ||
                        !!offer?.seasonalityId ||
                        !!offer?.typeId;

                      if (hasAnyValue) {
                        Modal.confirm({
                          title: 'Confirmar remoção',
                          content: 'Deseja realmente apagar esta seção?',
                          okText: 'Sim',
                          cancelText: 'Não',
                          maskClosable: false,
                          onOk: () => handleRemoveSectionOffer(index),
                        });
                      } else {
                        handleRemoveSectionOffer(index);
                      }
                    }}
                  >
                    Remover
                  </Button>
                )}
              </Col>
            </Row>
            <Divider style={{ margin: '0' }} />
            <Row gutter={[16, 16]} align='top' style={{ padding: '12px 16px' }}>
              <Col span={6}>
                <CurrencyController
                  control={control}
                  name={`offers.${index}.fullPrice`}
                  placeholder='Ex.: 99,90'
                  label='Valor da oferta'
                  maxLength={10}
                  required
                  reserveErrorSpace
                  disabled={isReadOnly}
                />
              </Col>
              <Col span={6}>
                <SelectController
                  label='Tipo de venda'
                  required
                  options={
                    ruleState?.optionsByType.offerType?.map(option => ({
                      label: `${option.value} - ${option.label}`,
                      value: option.value,
                    })) || []
                  }
                  control={control}
                  name={`offers.${index}.typeId`}
                  placeholder='Selecione'
                  allowClear
                  showSearch
                  reserveErrorSpace
                  disabled={isReadOnly || !!ruleState?.disabledByType.offerType}
                  onChange={value => handleTasChange(index, 'offerType', value)}
                />
              </Col>
              <Col span={6}>
                <SelectController
                  label='Aquisição'
                  required
                  options={
                    ruleState?.optionsByType.acquisition?.map(option => ({
                      label: `${option.value} - ${option.label}`,
                      value: option.value,
                    })) || []
                  }
                  control={control}
                  name={`offers.${index}.acquisitionId`}
                  placeholder='Selecione'
                  allowClear
                  showSearch
                  reserveErrorSpace
                  disabled={
                    isReadOnly || !!ruleState?.disabledByType.acquisition
                  }
                  onChange={value =>
                    handleTasChange(index, 'acquisition', value)
                  }
                />
              </Col>
              <Col span={6}>
                <SelectController
                  label='Sazonalidade'
                  required
                  options={
                    ruleState?.optionsByType.seasonality?.map(option => ({
                      label: `${option.value} - ${option.label}`,
                      value: option.value,
                    })) || []
                  }
                  control={control}
                  name={`offers.${index}.seasonalityId`}
                  placeholder='Selecione'
                  allowClear
                  showSearch
                  reserveErrorSpace
                  disabled={
                    isReadOnly || !!ruleState?.disabledByType.seasonality
                  }
                  onChange={value =>
                    handleTasChange(index, 'seasonality', value)
                  }
                />
              </Col>
            </Row>

            {isDuplicate && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#2a1215',
                  border: '1px solid #a61d24',
                  borderRadius: '4px',
                  color: '#ff4d4f',
                  fontSize: '14px',
                }}
              >
                Tipo de oferta, aquisição e sazonalidade duplicados. Favor
                alterar os parâmetros.
              </div>
            )}
          </div>
        );
      })}

      {showAddButton && (
        <Button
          type='dashed'
          onClick={() =>
            handleAddSectionOffer({
              fullPrice: '',
              acquisitionId: isMonthly ? MONTHLY_ACQUISITION_ID : '',
              seasonalityId: isMonthly ? MONTHLY_SEASONALITY_ID : '',
              typeId: '',
              isFallbackOffer: false,
            })
          }
          disabled={fields.length >= 20}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginBottom: '24px' }}
        >
          Adicionar nova oferta
        </Button>
      )}
    </>
  );
}
